import React, { useState, useEffect, useMemo } from 'react';
import { useConversation } from '@elevenlabs/react';
import './ChatHistory.scss';
import { Conversation } from '../api/getConversation';
import Utils from '@/utils/util';
import { ReactComponent as MenuBackIcon } from '@/assets/img/menu-back-icon.svg';
import { t } from 'i18next';
import { ReactComponent as Delete } from '@/assets/img/delete-icon-v2.svg';
import { ActionMenu } from '@/components/Elements/ActionMenu';
import ConfirmationDialogV2 from '@/components/Elements/ConfirmationDialog/ConfirmationDialogV2';
import { ConversationDetail } from './ChatAssistantV1';

type ChatHistoryProps = {
  onConversationSelected?: (conversationDetail: ConversationDetail) => void;
  onBack: () => void;
  conversations?: Conversation[];
  onDeleteConversation?: (conversationId: string) => void;
};

const DAY_IN_SECONDS = 86400;

export const ChatHistory: React.FC<ChatHistoryProps> = ({
  onConversationSelected,
  conversations = [],
  onBack,
  onDeleteConversation,
}) => {
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [selectedConversationId, setSelectedConversationId] = useState<string | null>(null);

  const groupedConversations = useMemo(() => {
    const now = Math.floor(Date.now() / 1000); // Current time in seconds

    return {
      recent: conversations.filter(
        (conv) => now - conv.start_time_unix_secs <= DAY_IN_SECONDS * 7 // Last 7 days
      ),
      old: conversations.filter((conv) => now - conv.start_time_unix_secs > DAY_IN_SECONDS * 7),
    };
  }, [conversations]);

  const handleConversationClick = async (
    conversationId: string,
    action: 'view' | 'call' | 'chat'
  ) => {
    try {
      onConversationSelected &&
        onConversationSelected({
          conversationId,
          chatType: action,
        });
    } catch (error) {
      console.error('Failed to fetch transcripts:', error);
    }
  };

  const handleDeleteClick = (conversationId: string) => {
    setSelectedConversationId(conversationId);
    setShowDeleteConfirmation(true);
  };

  const handleConfirmDelete = () => {
    if (selectedConversationId && onDeleteConversation) {
      onDeleteConversation(selectedConversationId);
    }
    setShowDeleteConfirmation(false);
    setSelectedConversationId(null);
  };

  const ConversationItem = ({ conv }: { conv: Conversation }) => (
    <div className="conversation-item">
      <div className="conversation-content">
        <div className="conversation-title" title={conv.call_summary_title}>
          {conv.call_summary_title}
        </div>
        <div className="conversation-date">
          {Utils.formatDateStringTz(
            new Date(conv.start_time_unix_secs * 1000).toISOString(),
            undefined,
            'MMM DD, yyyy hh:mm a'
          )}
        </div>
      </div>
      <ActionMenu
        options={[
          {
            label: t('View Chat'),
            onClickHandler: () => handleConversationClick(conv.conversation_id, 'view'),
            itemClassName: '',
          },
          {
            label: t('Call Emily'),
            onClickHandler: () => handleConversationClick(conv.conversation_id, 'call'),
            itemClassName: '',
          },
          {
            label: t('Chat with Emily'),
            onClickHandler: () => handleConversationClick(conv.conversation_id, 'chat'),
            itemClassName: '',
          },
          {
            label: t('Delete'),
            icon: Delete,
            onClickHandler: () => handleDeleteClick(conv.conversation_id),
            itemClassName: '',
          },
        ]}
      />
    </div>
  );

  return (
    <div className="chat-history">
      <div
        className="menu-back-nav"
        onClick={() => {
          onBack();
        }}
      >
        <MenuBackIcon /> <span>{t('Back')}</span>
      </div>
      <h2 className="chat-history-title">Chat History</h2>
      <div className="chat-history-container">
        <div className="conversations-list">
          {groupedConversations.recent.length > 0 && (
            <div className="conversation-group">
              <h3 className="group-title">Recent Conversations</h3>
              {groupedConversations.recent.map(
                (conv) =>
                  conv.call_summary_title && (
                    <ConversationItem key={conv.conversation_id} conv={conv} />
                  )
              )}
            </div>
          )}

          {groupedConversations.old.length > 0 && (
            <div className="conversation-group">
              <h3 className="group-title">Older Conversations</h3>
              {groupedConversations.old.map(
                (conv) =>
                  conv.call_summary_title && (
                    <ConversationItem key={conv.conversation_id} conv={conv} />
                  )
              )}
            </div>
          )}

          {conversations.length === 0 && (
            <div className="no-conversations">No conversations found</div>
          )}
        </div>
      </div>

      <ConfirmationDialogV2
        open={showDeleteConfirmation}
        onCancel={() => setShowDeleteConfirmation(false)}
        onConfirm={handleConfirmDelete}
        description=""
        title={t('Delete Conversation')}
        yesButtonLabel={t('Delete')}
        noButtonLabel={t('Cancel')}
      >
        <div className="remove-service-confirmation">
          <p className="message big">Are you sure you want to delete this conversation?</p>
        </div>
      </ConfirmationDialogV2>
    </div>
  );
};
